import {type RefCallback, useCallback} from 'react'

import type {BoardFocusPosition} from '../focus/types'
import {useRovingFocus} from '../focus/use-roving-focus'
import {useElementRef} from '../use-element-ref'
import {type PagingSentinelOptions, type PagingSentinelProps, usePagingSentinel} from './use-paging-sentinel'

export type PagingStopOptions = PagingSentinelOptions & {
  /** Where this direction's ghost sits on the board, as the focus model names it. */
  position: BoardFocusPosition
  /** The caller's name for this direction, which is what a reader arriving here hears. */
  label: string
}

/**
 * One paging direction's ghost: the sentinel whose arrival in view asks for the next page,
 * and that same element's share of the board's roving tabindex.
 *
 * **Nothing here is a control.** The ghost is still text saying more exists, it is not
 * pressed, and it takes no tab stop of its own. What it takes is the board's one stop,
 * while the board's position is on it, which is what a roving tabindex means. Putting a
 * `tabindex` of `0` on non-interactive text instead would give the board two ways in and
 * put an element nobody can operate into the page's tab order.
 *
 * **It is named, and that is the point of reaching it at all.** The board pages by being
 * scrolled, so `loadMoreItems` and its two siblings are the only way a reader is told a
 * direction has more, and scrolling a container announces nothing. A `group` carrying that
 * wording is what a reader hears on arriving, in the state where the ghost's own text is on
 * screen and in the state where a page is in flight and skeletons have replaced it. The
 * element has to survive that swap, or focus would fall to the document the moment the
 * page the reader asked for began loading.
 *
 * Both refs go on the one element, because they are two questions about the same box: has
 * it come into view, and is it where focus is.
 */
export function usePagingStop({position, label, ...sentinel}: PagingStopOptions): PagingSentinelProps {
  const {ref: elementRef, setRef} = useElementRef()
  const setSentinelRef = usePagingSentinel(sentinel)
  const {isRovingItem, onFocus} = useRovingFocus(position, elementRef)

  const ref = useCallback<RefCallback<HTMLElement>>(
    element => {
      setRef(element)
      setSentinelRef(element)
    },
    [setRef, setSentinelRef],
  )

  return {
    ref,
    role: 'group',
    'aria-label': label,
    // `-1` at rest rather than absent, so clicking the ghost moves the board's position
    // onto it, exactly as clicking a card does.
    tabIndex: isRovingItem ? 0 : -1,
    onFocus,
  }
}
